import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DeckSettings from '../components/DeckSettings';
import CardForm from '../components/CardForm';
import CardPreview from '../components/CardPreview';
import { getDeck, updateDeck, createCard, deleteCard, updateCard } from '../services/decks';

function EditDeck() {
  const { id } = useParams();
  const [deck, setDeck] = useState(null);
  const [openCardForm, setOpenCardForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setIsSaving] = useState(false);

  useEffect(() => {
    loadDeck();
  }, [id]);

  const loadDeck = async () => {
    try {
      const data = await getDeck(id);
      //console.log('Deck chargé:', data); // Pour déboguer
      setDeck(data);
    } catch (error) {
      console.error('Erreur lors du chargement du paquet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (updatedData) => {
    setIsSaving(true);
    try {
      let formData;
      
      // Si updatedData est déjà un FormData, l'utiliser directement
      if (updatedData instanceof FormData) {
        formData = updatedData;
      } else {
        // Sinon créer un nouveau FormData
        formData = new FormData();
        Object.entries(updatedData).forEach(([key, value]) => {
          if (value !== undefined) {
            formData.append(key, value);
          }
        });
      }

      const data = await updateDeck(id, formData);
      setDeck(data);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCardSubmit = async (cardData) => {
    try {
      const formData = new FormData();
      
      // Log des données avant envoi
      //console.log('Données de la carte à modifier:', cardData);
      //console.log('Carte en cours d\'édition:', editingCard);

      // Ajouter les images seulement si elles ont été modifiées
      if (cardData.frontImage) {
        formData.append('frontImage', cardData.frontImage);
      }
      if (cardData.backImage) {
        formData.append('backImage', cardData.backImage);
      }

      // Toujours ajouter le texte et les couleurs
      formData.append('front', cardData.front);
      formData.append('back', cardData.back);
      formData.append('frontColor', cardData.frontColor);
      formData.append('backColor', cardData.backColor);

      // Ajouter les URLs des images existantes si elles n'ont pas été modifiées
      if (editingCard) {
        if (!cardData.frontImage && editingCard.frontImageUrl) {
          formData.append('frontImageUrl', editingCard.frontImageUrl);
        }
        if (!cardData.backImage && editingCard.backImageUrl) {
          formData.append('backImageUrl', editingCard.backImageUrl);
        }
      }

      if (editingCard) {
        //console.log('FormData avant envoi:', Object.fromEntries(formData.entries()));
        await updateCard(id, editingCard.id, formData);
        //console.log('Réponse updateCard:', result);
      } else {
        // Création d'une nouvelle carte
        await createCard(id, formData);
      }

      // Recharger le deck et mettre à jour l'interface
      const updatedDeck = await getDeck(id);
      setDeck(updatedDeck);

      // Fermer le formulaire et réinitialiser l'état d'édition
      setOpenCardForm(false);
      setEditingCard(null);

    } catch (error) {
      console.error('Erreur détaillée:', error.response?.data || error);
    }
  };

  const handleDeleteCard = async () => {
    if (!cardToDelete) return;
    
    try {
      await deleteCard(id, cardToDelete.id);
      setDeleteConfirmOpen(false);
      setCardToDelete(null);
      loadDeck();
    } catch (error) {
      console.error('Erreur lors de la suppression de la carte:', error);
    }
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setOpenCardForm(true);
  };

  if (isLoading) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <DeckSettings 
        deck={deck} 
        onUpdate={handleUpdate}
      />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          Cartes ({deck.cards.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCardForm(true)}
        >
          Ajouter une carte
        </Button>
      </Box>

      <Grid container spacing={2}>
        {deck.cards.map(card => (
          <Grid item xs={12} sm={6} md={4} key={card.id}>
            <Box 
              sx={{ 
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9
                }
              }}
              onClick={() => handleEditCard(card)}
            >
              <CardPreview card={card} height={200} />
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation(); // Empêche le déclenchement du onClick du parent
                  setCardToDelete(card);
                  setDeleteConfirmOpen(true);
                }}
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>

      <CardForm
        open={openCardForm}
        onClose={() => {
          setOpenCardForm(false);
          setEditingCard(null);
        }}
        onSubmit={handleCardSubmit}
        initialCard={editingCard}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette carte ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleDeleteCard} color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default EditDeck; 